import Fragment from 'ember-data-model-fragments/fragment';
import type { Type } from '@warp-drive/core/types/symbols';
import { attr } from '@warp-drive/legacy/model';
import type { StatefulArray } from 'ember-data-model-fragments/array/stateful';
import { array } from 'ember-data-model-fragments/attributes';

// Proto: enrolments.NZDexRequest.
export default class DexRequest extends Fragment {
  declare [Type]: 'enrolments/nz-moe/dex-request';

  // applicationId provides a way to match up the returned responses with the
  // matching applicants.
  @attr('string', { defaultValue: '' })
  declare applicationId: string;

  // personId specifies the linked person within Hero that this request is
  // being performed for.
  @attr('string', { defaultValue: '' })
  declare personId: string;

  // personEnrolmentId specifies the linked person enrolment within Hero that
  // this request is being performed for.
  @attr('string', { defaultValue: '' })
  declare personEnrolmentId: string;

  // requesterUsername provides the username of the staff member at the
  // school who performed the request.
  @attr('string', { defaultValue: '' })
  declare requesterUsername: string;

  // Get/Check NSN fields
  // providerId specifies the Ministry of Education's code for the School
  // the Learner was (or is) in.
  @attr('number', { defaultValue: 0 })
  declare providerId: number;

  // nsn specifies the Learner's National Student Number.
  @attr('string', { defaultValue: '' })
  declare nsn: string;

  // birthDate specifies the Learner's date of birth.
  @attr('string')
  declare birthDate: string | undefined;

  // firstName specifies the Learner's First Name.
  @attr('string', { defaultValue: '' })
  declare firstName: string;

  // lastName specifies the Learner's Last Name.
  @attr('string', { defaultValue: '' })
  declare lastName: string;

  // gender specifies the Learner's gender.
  @attr('string', { defaultValue: '' })
  declare gender: string;

  // Arrival Upload fields
  // verificationDocumentId is the ID of the identity verification document.
  @attr('string', { defaultValue: '' })
  declare verificationDocumentId: string;

  // verificationDocumentExpiryDate is the expiry date of the identity
  // verification document.
  @attr('string')
  declare verificationDocumentExpiryDate: string | undefined;

  // verificationDocumentType is the MOE code representing the type of
  // identity verification document.
  @attr('string', { defaultValue: '' })
  declare verificationDocumentType: string;

  // startDate is the starting date of the Learner's enrolment at the School
  // in question.
  @attr('string')
  declare startDate: string | undefined;

  // tuitionFee is the weekly tuition fee amount in New Zealand cents
  @attr('number', { defaultValue: 0 })
  declare tuitionFee: number;

  // fte is the proportion of a full-time equivalent enrolment, as a whole
  // number percentage.
  @attr('number', { defaultValue: 0 })
  declare fte: number;

  // eligibilityStatus is the MOE eligibiilty status code
  @attr('string', { defaultValue: '' })
  declare eligibilityStatus: string;

  // yearLevel is the instructional year level of the student
  @attr('number', { defaultValue: 0 })
  declare yearLevel: number;

  // exchangeScheme is the MoE code for the Exchange Scheme which applies to
  // the Learner, if any.
  @attr('string', { defaultValue: '' })
  declare exchangeScheme: string;

  // citizenship is the 3-letter country code for the Learner's country of
  // citizenship
  @attr('string', { defaultValue: '' })
  declare citizenship: string;

  // ethnicities is a list of MOE codes for the Learner's ethnicities
  @array('string')
  declare ethnicities: StatefulArray<string>;

  // iwis is a list of MOE codes for the Learner's iwi affiliation(s)
  @array('string')
  declare iwis: StatefulArray<string>;

  // address1 is the first line of the Learner's home address
  // ("unstructured thoroughfare" - street address)
  @attr('string', { defaultValue: '' })
  declare address1: string;

  // address2 is the second line of the Learner's home address
  // ("dependent locality" - suburb)
  @attr('string', { defaultValue: '' })
  declare address2: string;

  // address3 is the third line of the Learner's home address
  // ("locality" - city/town)
  @attr('string', { defaultValue: '' })
  declare address3: string;

  // postCode is the post code of the Learner's home address
  @attr('string', { defaultValue: '' })
  declare postCode: string;

  // Leaver Upload fields
  // IsConfidential states whether the data is confidential or not
  @attr('boolean', { defaultValue: false })
  declare isConfidential: boolean;

  // LastAttendanceDate provides the Learner's last recorded attendance date
  // in ENROL
  @attr('string')
  declare lastAttendanceDate: string | undefined;

  // AcademicFlag indicates that the Learner's next school should contact
  // this one about an academic issue.
  @attr('boolean', { defaultValue: false })
  declare academicFlag: boolean;

  // AttendanceFlag indicates that the Learner's next school should contact
  // this one about an attendance issue.
  @attr('boolean', { defaultValue: false })
  declare attendanceFlag: boolean;

  // BehaviouralFlag indicates that the Learner's next school should contact
  // this one about a behavioural issue.
  @attr('boolean', { defaultValue: false })
  declare behaviouralFlag: boolean;

  // CustodialFlag indicates that the Learner's next school should contact
  // this one about a custodial issue.
  @attr('boolean', { defaultValue: false })
  declare custodialFlag: boolean;

  // HealthFlag indicates that the Learner's next school should contact
  // this one about a health issue.
  @attr('boolean', { defaultValue: false })
  declare healthFlag: boolean;

  // PersonalFlag indicates that the Learner's next school should contact
  // this one about a personal issue.
  @attr('boolean', { defaultValue: false })
  declare personalFlag: boolean;

  // Reason is the MoE code for the Learner's departure reason
  @attr('string', { defaultValue: '' })
  declare reason: string;

  // PostSchoolActivity is the MoE code for the Learner's intended
  // post-school activity.
  @attr('string', { defaultValue: '' })
  declare postSchoolActivity: string;

  // NextArea is the MoE "territorial local authority code" for the Learner's
  // destination area, if applicable.
  @attr('string', { defaultValue: '' })
  declare nextArea: string;

  // NextSchools is a list of MoE unique provider IDs for Schools which the
  // Learner may attend after this one.
  @array('number')
  declare nextSchools: StatefulArray<number>;

  // Roll Return upload fields
  // returnId is the unique identifier for the original Roll Return entity in the roll returns service.
  @attr('string', { defaultValue: '' })
  declare returnId: string;

  // Attendance Survey upload fields
  // fromDate is the start date of the period this survey covers, in YYYY-MM-DD format.
  @attr('string')
  declare fromDate: string | undefined;

  // toDate is the end date of the period this survey covers, in YYYY-MM-DD format.
  @attr('string')
  declare toDate: string | undefined;

  // surveyContent is the content of the attendance survey (CSV) to be uploaded.
  @attr('string', { defaultValue: '' })
  declare surveyContent: string;

  // uploadType specifies the type of file being uploaded to dex.
  @attr('number', { defaultValue: 0 })
  declare uploadType: number;
}
